import * as Yup from 'yup';

import { type IAddressFormValues, requiredAddressValidationSchema } from 'src/common/validation/address';
import { paymentContactValidationSchema } from 'src/common/validation/contact';
import { FIELD_IS_REQUIRED } from 'src/common/validation/errors';
import type { IPaymentContact } from 'src/types/IPaymentContact';
import type { IVendorLeadBillingInfo } from 'src/types/vendors/IVendorLeadBillingInfo';
import { PaymentMethod } from 'src/types/vendors/PaymentMethod';
import { VendorPaymentTerms } from 'src/types/vendors/VendorPaymentTerms';

export type FormVendorProductLeadBillingInfoValues = {
  paymentContact: IPaymentContact;
  billingAddress: IAddressFormValues;
  paymentTerms: VendorPaymentTerms;
  paymentMethod: PaymentMethod;

  bankAccountNumber: string | null;
  bankAccountNumberConfirm: string | null;
  routingNumber: string | null;
};

export const vendorProductLeadBillingInfoPublishValidationSchema = Yup.object<FormVendorProductLeadBillingInfoValues>({
  paymentContact: paymentContactValidationSchema,
  billingAddress: requiredAddressValidationSchema,

  paymentTerms: Yup.mixed<VendorPaymentTerms>().required(FIELD_IS_REQUIRED).default(VendorPaymentTerms.Net15),
  paymentMethod: Yup.mixed<PaymentMethod>().required(FIELD_IS_REQUIRED).default(PaymentMethod.Ach),

  bankAccountNumber: Yup.string()
    .test(
      'bankAccountNumber',
      'The field must have digits only',
      (bankAccountNumber?: string | null) => !bankAccountNumber || /^\d+$/.test(bankAccountNumber)
    )
    .min(6, 'The field must have minimum 6 digits')
    .max(17, 'The field must have maximum 17 digits')
    .transform((value) => (value ? value : null))
    .when('paymentMethod', {
      is: (paymentMethod: PaymentMethod | null) => paymentMethod === PaymentMethod.Ach,
      then: (finalSchema) => finalSchema.required('Field is required for this payment method'),
      otherwise: (finalSchema) => finalSchema.notRequired(),
    })
    .default(null),

  bankAccountNumberConfirm: Yup.string()
    .when('usePrimaryBillingInfo', {
      is: (usePrimaryBillingInfo: boolean) => usePrimaryBillingInfo,
      otherwise: (schema) =>
        schema.test(
          'bankAccountNumberConfirm',
          'Bank account do not match',
          (bankAccountNumberConfirm: string | null | undefined, context) => {
            return !context.parent.bankAccountNumber || bankAccountNumberConfirm === context.parent.bankAccountNumber;
          }
        ),
    })
    .transform((value) => value || null)
    .notRequired()
    .nullable()
    .default(null),

  routingNumber: Yup.string()
    .test(
      'routingNumber',
      'The field must have digits only',
      (routingNumber?: string | null) => !routingNumber || /^\d+$/.test(routingNumber)
    )
    .length(9, 'The field must be exactly 9 numbers')
    .transform((value) => (value ? value : null))
    .when('paymentMethod', {
      is: (paymentMethod: PaymentMethod | null) => paymentMethod === PaymentMethod.Ach,
      then: (finalSchema) => finalSchema.required('Field is required for this payment method'),
      otherwise: (finalSchema) => finalSchema.notRequired(),
    })
    .default(null),
});

export function toVendorLeadBillinInfoInitialValues(
  vendorLeadBillingInfo: IVendorLeadBillingInfo
): FormVendorProductLeadBillingInfoValues {
  return {
    billingAddress: vendorLeadBillingInfo.billingAddress,
    paymentContact: vendorLeadBillingInfo.paymentContact ?? {
      firstName: null,
      lastName: null,
      emailAddress: null,
      phoneNumber: null,
    },

    paymentTerms: Object.values(VendorPaymentTerms).includes(vendorLeadBillingInfo.paymentTerms)
      ? vendorLeadBillingInfo.paymentTerms
      : (null as unknown as VendorPaymentTerms),

    paymentMethod: Object.values(PaymentMethod).includes(vendorLeadBillingInfo.paymentMethod)
      ? vendorLeadBillingInfo.paymentMethod
      : (null as unknown as PaymentMethod),

    bankAccountNumber: vendorLeadBillingInfo.bankAccount?.accountNumber ?? null,
    bankAccountNumberConfirm: vendorLeadBillingInfo.bankAccount?.accountNumber ?? null,
    routingNumber: vendorLeadBillingInfo.bankAccount?.routingNumber ?? null,
  };
}
